import { defineStore } from 'pinia';
import {
  EToastPlacement,
  type IToast,
  type IToastsStrapiResponse,
  type ETriggerId,
  type IToastAddOptions,
} from '~/types/toasts';
import { createPopulateString } from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';

const cashierRoutes = [
  'modal=deposit',
  'modal=buy_crypto',
  'modal=compoints',
  'modal=transaction_history',
  'modal=profile',
  'modal=withdraw',
];

export const useStoreToasts = defineStore('toasts', {
  state: () => ({
    toastsData: [] as IToast[],
    activeToasts: [] as IToast[],
    requestPromise: null as Promise<IToastsStrapiResponse> | null,
  }),

  getters: {
    topToasts(): IToast[] {
      return this.activeToasts.filter((toast) => toast.placement === EToastPlacement.Top);
    },
    bottomToasts(): IToast[] {
      return this.activeToasts.filter((toast) => toast.placement === EToastPlacement.Bottom);
    },
  },

  actions: {
    async loadToasts() {
      if (!this.requestPromise) {
        const populateString = createPopulateString(['groupFilter']);

        this.requestPromise = strapi()
          .get('toasts', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        this.toastsData = response.data;

        return true;
      } else {
        return false;
      }
    },
    findIfAceeptableByFilters(toast: IToast) {
      const route = useRoute();

      const isAcceptableByGroups = toast.groupFilter
        ? findIfPassesByGroups(toast.groupFilter.includedGroups, toast.groupFilter.excludedGroups)
        : true;
      const isAcceptableByGamePage = !toast.isAvailableInGamePage
        ? !window.location.href.includes('/games/')
        : true;
      const isAcceptableByDepModal = !toast.isAvailableInCashier
        ? !cashierRoutes.some((modalRoute) => route.fullPath.includes(modalRoute))
        : true;

      return isAcceptableByGroups && isAcceptableByGamePage && isAcceptableByDepModal;
    },
    add(triggerId: ETriggerId, options?: IToastAddOptions) {
      const toastsWithRequiredID = this.toastsData.filter((toast) => toast.triggerId === triggerId);

      toastsWithRequiredID.forEach((toast) => {
        if (this.findIfAceeptableByFilters(toast)) {
          let message = toast.message;

          if (options?.preMessage) {
            message = `${options.preMessage} ${message}`;
          }

          if (options?.postMessage) {
            message = `${message} ${options.postMessage}`;
          }

          this.activeToasts.push({ ...toast, message });

          if (toast.duration > 0) {
            setTimeout(() => {
              this.remove(toast.id);
            }, toast.duration * 1000);
          }

          if (toast.groupToAddOnRead) {
            useStoreSsPlayer().updatePlayerGroups({ add: [toast.groupToAddOnRead] });
          }
        }
      });
    },
    remove(id: number) {
      const index = this.activeToasts.findIndex((t) => t.id === id);
      this.activeToasts.splice(index, 1);
    },
  },
});
